import { useState, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, Mail, ChevronDown, Award, Sparkles, ShieldCheck } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

import greenBag from "@/assets/vasavi_green_sortex.jpg";
import blueBag from "@/assets/vasavi_sortex_superior.jpg";
import maroonBag from "@/assets/vasavi_maroon_sortex.jpg";
import lowGiBag from "@/assets/low_gi_rice.jpg";

const Particles = () => {
  const bits = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {bits.map((_, i) => {
        const left = Math.random() * 100;
        const dur = 14 + Math.random() * 16;
        const delay = Math.random() * 14;
        const scale = 0.6 + Math.random() * 1.1;
        return (
          <span
            key={i}
            className="rice-particle"
            style={{
              left: `${left}%`,
              animationDuration: `${dur}s`,
              animationDelay: `${delay}s`,
              transform: `scale(${scale})`,
            }}
          />
        );
      })}
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Pack Variants for Concept 1 Switcher
  const packVariants = [
    { id: "green", name: "Emerald Green 26kg", tag: "Signature Grade", img: greenBag, color: "#2d6a4f" },
    { id: "blue", name: "Royal Blue 26kg", tag: "Sortex Superior", img: blueBag, color: "#1e3a8a" },
    { id: "maroon", name: "Royal Maroon 26kg", tag: "Traditional Taste", img: maroonBag, color: "#701a2b" },
    { id: "lowgi", name: "Low GI Care 1kg", tag: "Diabetic Friendly", img: lowGiBag, color: "#92400e" },
  ];

  const [selectedPack, setSelectedPack] = useState(packVariants[0]);

  const scrollToAbout = () => {
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const btns = [
    { label: t("hero.whatsappBtn"), icon: MessageCircle, href: `https://wa.me/${CONTACT.whatsapp}?text=Hello%20Gupta%20Enterprises%2C%20I%27d%20like%20to%20enquire%20about%20Vasavi%20Gold%20rice.`, primary: true, testid: "hero-whatsapp-btn" },
    { label: t("hero.callBtn"), icon: Phone, href: `tel:${CONTACT.phoneDial}`, testid: "hero-call-btn" },
    { label: t("hero.enquiryBtn"), icon: Mail, href: "#contact", testid: "hero-enquiry-btn" },
  ];

  const headlineLines = [
    t("hero.headlineLine1"),
    t("hero.headlineLine2"),
    t("hero.headlineLine3"),
  ];

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden pb-12 pt-20 sm:pt-24 md:pt-28" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.heroField}
          alt="Sunrise over lush green rice fields"
          className="h-[120%] w-full object-cover"
          fetchPriority="high"
        />
        {/* Top Scrim Gradient - anchors the grand header with optimal contrast */}
        <div className="absolute top-0 left-0 right-0 h-44 bg-gradient-to-b from-[#1b4332]/35 via-[#f8fff8]/40 to-transparent z-10 pointer-events-none" />
        
        {/* Light, brand-tinted wash — keeps it airy, never dark */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8fff8]/80 via-[#f8fff8]/50 to-[#f8fff8]/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#f8fff8]/80 via-[#f8fff8]/40 to-transparent" />
      </motion.div>

      <Particles />

      {/* Content Container */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-4 sm:pt-10 md:pt-14 flex flex-col justify-center min-h-[calc(100svh-100px)]"
      >
        
        {/* ======================================================== */}
        {/* MOBILE CONCEPT A: Integrated Hero Spotlight (<1024px)     */}
        {/* ======================================================== */}
        <div className="flex flex-col items-center text-center lg:hidden">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-2 mb-2"
          >
            <span className="h-px w-6 bg-[#d4af37]" />
            <span className="eyebrow text-[11px]">{t("hero.since")}</span>
            <span className="h-px w-6 bg-[#d4af37]" />
          </motion.div>

          {/* Punchy Headline */}
          <h1 className="font-display font-semibold text-[#1c2b22] text-3xl sm:text-4xl tracking-tight leading-[1.05] max-w-sm">
            {headlineLines.map((line, i) => (
              <span key={i} className="block">
                {i === 2 ? (
                  <>
                    {line}<span className="text-[#40916c]">.</span>
                  </>
                ) : (
                  line
                )}
              </span>
            ))}
          </h1>

          {/* Center 3D Floating Bag Spotlight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="relative mt-5 mb-3 flex flex-col items-center justify-center w-full"
          >
            {/* Ambient Radial Spotlight Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-gradient-to-tr from-[#d4af37]/40 via-[#40916c]/25 to-transparent blur-2xl pointer-events-none" />

            {/* Floating Bag — no white card box, floats cleanly over spotlight */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-20"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedPack.id}
                  initial={{ opacity: 0, scale: 0.9, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -8 }}
                  transition={{ duration: 0.35 }}
                  className="relative flex items-center justify-center"
                >
                  {/* Clean frameless float — no boxy white card */}
                  <img
                    src={selectedPack.img}
                    alt={`Vasavi Gold ${selectedPack.name}`}
                    className="h-56 sm:h-68 w-auto object-contain drop-shadow-2xl"
                    style={{ filter: "drop-shadow(0 20px 40px rgba(212,175,55,0.30)) drop-shadow(0 8px 20px rgba(0,0,0,0.18))" }}
                  />
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* Gold Badge — sits BELOW the bag, ABOVE the pack switcher, never overlaps headline */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedPack.id + "_badge"}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3 }}
                className="mt-3 whitespace-nowrap bg-gradient-to-r from-[#d4af37] to-[#b89628] text-white text-[10px] font-bold px-3.5 py-1.5 rounded-full shadow-lg border border-white/50 flex items-center gap-1.5 z-30"
              >
                <Sparkles size={11} className="shrink-0" /> Vasavi Gold {selectedPack.name}
              </motion.div>
            </AnimatePresence>

            {/* Interactive Pack Selector — Dots Only (compact, no text labels) */}
            <div className="relative z-30 mt-2.5 flex items-center justify-center gap-2 bg-white/85 backdrop-blur-md px-4 py-2 rounded-full border border-[#40916c]/20 shadow-md">
              {packVariants.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPack(p)}
                  title={p.name}
                  className={`flex items-center justify-center transition-all ${
                    selectedPack.id === p.id
                      ? "scale-125"
                      : "opacity-60 hover:opacity-90"
                  }`}
                >
                  <span
                    className={`rounded-full border-2 transition-all ${
                      selectedPack.id === p.id
                        ? "h-4 w-4 border-white shadow-md"
                        : "h-3 w-3 border-white/40"
                    }`}
                    style={{ backgroundColor: p.color }}
                  />
                </button>
              ))}
              <span className="ml-1 font-body text-[10px] text-[#3a4a41] font-semibold">{selectedPack.tag}</span>
            </div>
          </motion.div>

          {/* Compact Actions directly under the Spotlight Bag (Everything Above the Fold) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex items-center justify-center gap-2.5 w-full max-w-xs"
          >
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=Hello%20Gupta%20Enterprises%2C%20I%27d%20like%20to%20enquire%20about%20Vasavi%20Gold%20rice.`}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#40916c] hover:bg-[#2d6a4f] text-white font-body font-semibold text-xs py-3 shadow-lg shadow-[#40916c]/20 transition-all active:scale-95"
            >
              <MessageCircle size={15} />
              <span>WhatsApp Us</span>
            </a>
            <a
              href={`tel:${CONTACT.phoneDial}`}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white/90 backdrop-blur-md text-[#2d6a4f] border border-[#40916c]/30 font-body font-semibold text-xs py-3 shadow-sm transition-all active:scale-95"
            >
              <Phone size={15} />
              <span>Call Us</span>
            </a>
          </motion.div>

          {/* Quick Inline Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
            className="flex items-center justify-center gap-4 mt-3.5 pt-3 border-t border-[#40916c]/15 text-[11px] font-semibold text-[#2d6a4f]"
          >
            <div className="flex items-center gap-1">
              <Sparkles size={13} className="text-[#d4af37]" />
              <span>100% Sortexed</span>
            </div>
            <div className="flex items-center gap-1">
              <ShieldCheck size={13} className="text-[#40916c]" />
              <span>Guaranteed Purity</span>
            </div>
          </motion.div>
        </div>


        {/* ======================================================== */}
        {/* DESKTOP LAYOUT (>=1024px) - 2 Column Split Layout        */}
        {/* ======================================================== */}
        <div className="hidden lg:grid grid-cols-12 gap-8 items-center">
          
          {/* Desktop Left Column — Text & CTAs */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="h-px w-10 bg-[#d4af37]" />
              <span className="eyebrow">{t("hero.since")}</span>
            </motion.div>

            <h1 className="font-display font-semibold text-[#1c2b22] text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.98] max-w-4xl">
              {headlineLines.map((line, i) => (
                <span className="reveal-mask" key={i}>
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {i === 2 ? (
                      <>
                        {line}<span className="text-[#40916c]">.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.8 }}
              className="font-body text-[#33463b] text-base md:text-lg leading-relaxed max-w-xl mt-8"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="flex flex-wrap items-center gap-3.5 mt-10 max-w-xl"
            >
              {btns.map((b) => {
                const Icon = b.icon;
                return (
                  <a
                    key={b.label}
                    href={b.href}
                    target={b.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className={`inline-flex items-center gap-2.5 rounded-full font-body font-medium text-[15px] px-7 py-3.5 transition-all hover:-translate-y-0.5 ${
                      b.primary
                        ? "bg-[#40916c] text-white hover:bg-[#2d6a4f] hover:shadow-xl hover:shadow-[#40916c]/25"
                        : "bg-white/80 backdrop-blur-md text-[#2d6a4f] border border-[#40916c]/25 hover:bg-white hover:border-[#40916c]/50 shadow-sm"
                    }`}
                    data-testid={b.testid}
                  >
                    <Icon size={18} strokeWidth={2} />
                    {b.label}
                  </a>
                );
              })}
            </motion.div>

            {/* Quick Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.35, duration: 0.8 }}
              className="flex items-center gap-6 mt-10 pt-6 border-t border-[#40916c]/15"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-[#2d6a4f]">
                <Sparkles size={16} className="text-[#d4af37]" />
                <span>100% Sortexed Superior</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-[#2d6a4f]">
                <ShieldCheck size={16} className="text-[#40916c]" />
                <span>Guaranteed Purity</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-[#2d6a4f]">
                <Award size={16} className="text-[#d4af37]" />
                <span>50+ Years Legacy</span>
              </div>
            </motion.div>
          </div>

          {/* Desktop Right Column — 3D Spotlight Showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative flex flex-col items-center justify-center min-h-[540px]"
          >
            {/* Ambient Golden & Emerald Radial Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-to-tr from-[#d4af37]/40 via-[#40916c]/30 to-transparent blur-3xl pointer-events-none" />

            {/* Floating Bag Frame Container */}
            <div className="relative w-full max-w-md flex flex-col items-center justify-center">

              {/* 3D Floating Bag */}
              <motion.div
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-20 group"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedPack.id}
                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="relative flex items-center justify-center"
                  >
                    <div className="relative rounded-3xl bg-white/80 backdrop-blur-xl p-5 border-2 border-[#d4af37]/60 shadow-2xl shadow-[#d4af37]/25 hover:scale-105 transition-all duration-500">
                      <img
                        src={selectedPack.img}
                        alt={`Vasavi Gold ${selectedPack.name}`}
                        className="h-84 md:h-96 w-auto object-contain rounded-2xl drop-shadow-2xl"
                      />
                      {/* Gold Badge on Bag */}
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-[#d4af37] to-[#b89628] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-xl border border-white/50 flex items-center gap-1.5">
                        <Sparkles size={14} /> Vasavi Gold {selectedPack.name}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Floating Feature Badges around Bag */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="absolute -top-3 -right-8 bg-white/95 backdrop-blur-md text-[#2d6a4f] text-[11px] font-bold px-3.5 py-1.5 rounded-full shadow-xl border border-[#40916c]/30 z-30 flex items-center gap-1.5"
                >
                  <Sparkles size={13} className="text-[#d4af37]" /> 100% Sortexed
                </motion.div>

                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute bottom-6 -left-8 bg-white/95 backdrop-blur-md text-[#2d6a4f] text-[11px] font-bold px-3.5 py-1.5 rounded-full shadow-xl border border-[#40916c]/30 z-30 flex items-center gap-1.5"
                >
                  <ShieldCheck size={13} className="text-[#40916c]" /> 50+ Yrs Trust
                </motion.div>
              </motion.div>

              {/* Interactive Pack Selector Dots */}
              <div className="relative z-30 mt-8 flex items-center justify-center gap-2 bg-white/80 backdrop-blur-md p-2 rounded-full border border-[#40916c]/25 shadow-lg">
                {packVariants.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPack(p)}
                    onMouseEnter={() => setSelectedPack(p)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-body text-xs font-semibold transition-all ${
                      selectedPack.id === p.id
                        ? "bg-[#2d6a4f] text-white shadow-md scale-105"
                        : "text-[#3a4a41] hover:text-[#2d6a4f] hover:bg-white/60"
                    }`}
                  >
                    <span
                      className="h-3 w-3 rounded-full border border-white/60 shrink-0"
                      style={{ backgroundColor: p.color }}
                    />
                    <span>{p.name.split(" ")[0]} {p.name.split(" ")[1]}</span>
                  </button>
                ))}
              </div>

            </div>
          </motion.div>

        </div>

      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToAbout}
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 group"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <span className="font-body text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-[#40916c]/70">{t("hero.scroll")}</span>
        <span className="relative h-7 w-4 sm:h-9 sm:w-5 rounded-full border border-[#40916c]/40 flex justify-center pt-1.5">
          <span className="scroll-dot h-1.5 w-1.5 rounded-full bg-[#40916c]" />
        </span>
        <ChevronDown size={15} className="text-[#40916c]/50 -mt-1 group-hover:translate-y-0.5 transition-transform" />
      </motion.button>
    </section>
  );
};

export default Hero;
